<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, type PropType } from 'vue'
import { BarChartSorting, SolidFill, ColorHEX, emptyLine, FontSettings, AxisTickStrategies, Themes } from '@lightningchart/lcjs'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue'
const { hasIntermediateAccess } = useAuth();

interface TotalProjectConsumptionItem {
  cement1: number
  cement2: number
  additive1: number
  additive2: number
  additive3: number
  additive4: number
  additive5: number
  additive6: number
  accelerator: number
}

interface TotalConsumedMaterialOverviewFromPumpsItem {
  holeNo: number
  maxPressure: number
  cement1: number
  cement2: number
  additive1: number
  additive2: number
  additive3: number
  additive4: number
  additive5: number
  additive6: number
  accelerator: number
}
const props = defineProps({
  totalUsedMaterialFromEvents: {
    type: Object as PropType<TotalProjectConsumptionItem>,
    required: true,
    default: () => ({})
  },
  totalConsumedMaterialOverviewFromPumpsData: {
    type: Array as PropType<TotalConsumedMaterialOverviewFromPumpsItem[]>,
    required: true
  }
});

const getFromPumpsAdditiveValue = (index: number) => {
  if (!extractTotalMaterialConsumedFromPumps.value) return 0;
  const key = `additive${index}` as keyof typeof extractTotalMaterialConsumedFromPumps.value;
  return (extractTotalMaterialConsumedFromPumps.value[key] as number) || 0;
};
const getFromEventsAdditiveValue = (index: number) => {
  if (!totalMaterialFromEvents.value) return 0;
  const key = `additive${index}` as keyof typeof totalMaterialFromEvents.value;
  return (totalMaterialFromEvents.value[key] as number) || 0;
};
const fmt = (val: number | undefined) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
};


const extractTotalMaterialConsumedFromPumps = computed(() => {
  const total = {
    cement1: 0,
    cement2: 0,
    additive1: 0,
    additive2: 0,
    additive3: 0,
    additive4: 0,
    additive5: 0,
    additive6: 0,
    accelerator: 0
  };
  for (const item of props.totalConsumedMaterialOverviewFromPumpsData) 
  {
    total.cement1 += item.cement1 || 0;
    total.cement2 += item.cement2 || 0;
    total.additive1 += item.additive1 || 0;
    total.additive2 += item.additive2 || 0;
    total.additive3 += item.additive3 || 0;
    total.additive4 += item.additive4 || 0;
    total.additive5 += item.additive5 || 0;
    total.additive6 += item.additive6 || 0;
    total.accelerator += item.accelerator || 0;
  }
  return total;
});
const totalMaterialFromEvents = computed(() => {
  return props.totalUsedMaterialFromEvents
    ? props.totalUsedMaterialFromEvents
    : null;
    
});


const headers = [
  { title: 'Hole No.', key: 'holeNo', align: 'start' },
  { title: 'Max pressure', key: 'maxPressure', align: 'end' },
  { title: 'Cement 1', key: 'cement1', align: 'end' },
  { title: 'Cement 2', key: 'cement2', align: 'end' },
  { title: 'Total acc.', key: 'accelerator', align: 'end' },
  { title: 'Total additive 1', key: 'additive1', align: 'end' },
  { title: 'Total additive 2', key: 'additive2', align: 'end' },
  { title: 'Total additive 3', key: 'additive3', align: 'end' },
  { title: 'Total additive 4', key: 'additive4', align: 'end' },
  { title: 'Total additive 5', key: 'additive5', align: 'end' },
  { title: 'Total additive 6', key: 'additive6', align: 'end' },
] as const;

// --- Bar chart ---
const chartContainer = ref<HTMLDivElement>()
let chart: any = undefined

const barData = computed(() => {
  if (!props.totalConsumedMaterialOverviewFromPumpsData?.length) return []
  return props.totalConsumedMaterialOverviewFromPumpsData.map(item => ({
    category: `H${item.holeNo}`,
    value: (item.cement1 || 0) + (item.cement2 || 0)
  }))
})

const initChart = () => {
  if (!chartContainer.value) return
  const lc = useUseLightningCharts()

  const TEXT_DARK  = ColorHEX('#1e2d3d')
  const TEXT_MID   = ColorHEX('#4b5e6d')
  const PAGE_BG    = ColorHEX('#f5f5f5')

  const axisLabelFont = new FontSettings().setSize(11).setFamily('Arial').setWeight('normal')
  const titleFont     = new FontSettings().setSize(15).setFamily('Arial').setWeight('bold')

  chart = lc.BarChart({ container: chartContainer.value, theme: Themes.light })
    .setTitle('Total Cement per Hole (kg)')
    .setTitleFont(titleFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_DARK }))
    .setTitleEffect(false)
    .setBackgroundFillStyle(new SolidFill({ color: PAGE_BG }))
    .setBackgroundStrokeStyle(emptyLine)
    .setSeriesBackgroundFillStyle(new SolidFill({ color: ColorHEX('#ffffff') }))
    .setSeriesBackgroundEffect(false)
    .setCornerRadius(3)
    .setBarsMargin(0.05)
    .setBarsEffect(false)
    .setAnimationsEnabled(false)
    .setSorting(BarChartSorting.Disabled)
    .setValueLabels(undefined)
    .setCategoryLabels({
      labelFont: axisLabelFont,
      labelFillStyle: new SolidFill({ color: TEXT_MID })
    })

  chart.valueAxis
    .setTickStrategy(AxisTickStrategies.Numeric, (ticks: any) =>
      ticks.setMajorTickStyle((major: any) =>
        major
          .setLabelFont(axisLabelFont)
          .setLabelFillStyle(new SolidFill({ color: TEXT_MID }))
      )
    )
    .setTitle('kg')
    .setTitleFont(axisLabelFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_MID }))

  if (barData.value.length > 0) {
    applyChartData(barData.value)
  }
}

onMounted(() => {
  initChart()
})

onUnmounted(() => {
  chart?.dispose()
  chart = undefined
})

const applyChartData = (data: { category: string; value: number }[]) => {
  chart.setData(data)
  chart.getSeries().forEach((bar: any) => {
    bar.setFillStyle(new SolidFill({ color: ColorHEX('#041e43') }))
       .setStrokeStyle(emptyLine)
  })
}

watch(barData, (newData) => {
  if (!chart || !newData.length) return
  applyChartData(newData)
})

</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>

    <v-row class="mb-6">
      <!-- Pump Information -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Pump Information
            <span class="text-caption text-medium-emphasis text-lowercase font-weight-normal ml-2">Cumulative totals from active holes</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div v-if="extractTotalMaterialConsumedFromPumps">
              <v-row dense>
                <v-col cols="6" sm="4" class="mb-2">
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 1</div>
                  <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.cement1) }} <span class="text-caption text-medium-emphasis">kg</span></div>
                </v-col>
                <v-col cols="6" sm="4" class="mb-2">
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 2</div>
                  <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.cement2) }} <span class="text-caption text-medium-emphasis">kg</span></div>
                </v-col>
                <v-col cols="6" sm="4" class="mb-2" v-for="i in 6" :key="i">
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive {{ i }}</div>
                  <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(getFromPumpsAdditiveValue(i)) }} <span class="text-caption text-medium-emphasis">L</span></div>
                </v-col>
              </v-row>
            </div>
            <div v-else class="text-center text-medium-emphasis font-italic py-4">
              No pump data available
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Event Information -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Event Information
            <span class="text-caption text-medium-emphasis text-lowercase font-weight-normal ml-2">Based on weight cells</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div v-if="totalMaterialFromEvents">
              <v-row dense>
                <v-col cols="6" sm="4" class="mb-2">
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 1</div>
                  <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialFromEvents.cement1) }} <span class="text-caption text-medium-emphasis">kg</span></div>
                </v-col>
                <v-col cols="6" sm="4" class="mb-2">
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 2</div>
                  <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialFromEvents.cement2) }} <span class="text-caption text-medium-emphasis">kg</span></div>
                </v-col>
                <v-col cols="6" sm="4" class="mb-2" v-for="i in 6" :key="i">
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive {{ i }}</div>
                  <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(getFromEventsAdditiveValue(i)) }} <span class="text-caption text-medium-emphasis">L</span></div>
                </v-col>
              </v-row>
            </div>
            <div v-else class="text-center text-medium-emphasis font-italic py-4">
              No event data available
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Chart Section -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Cement Consumption per Hole
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <div ref="chartContainer" style="height: 350px; width: 100%;"></div>
      </v-card-text>
    </v-card>

    <!-- Detailed Table -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <div class="d-flex align-center">
          <span class="text-subtitle-1 font-weight-bold mr-3">Consumed Material overview</span>
          <v-chip size="small" variant="flat" color="blue-grey-lighten-5" class="text-blue-grey-darken-2 font-weight-bold">
            {{ props.totalConsumedMaterialOverviewFromPumpsData.length }} entries
          </v-chip>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="props.totalConsumedMaterialOverviewFromPumpsData"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <template v-slot:item.maxPressure="{ item }"><span class="font-monospace">{{ fmt(item.maxPressure) }}</span></template>
        <template v-slot:item.cement1="{ item }"><span class="font-monospace">{{ fmt(item.cement1) }}</span></template>
        <template v-slot:item.cement2="{ item }"><span class="font-monospace">{{ fmt(item.cement2) }}</span></template>
        <template v-slot:item.accelerator="{ item }"><span class="font-monospace">{{ fmt(item.accelerator) }}</span></template>
        <template v-slot:item.additive1="{ item }"><span class="font-monospace">{{ fmt(item.additive1) }}</span></template>
        <template v-slot:item.additive2="{ item }"><span class="font-monospace">{{ fmt(item.additive2) }}</span></template>
        <template v-slot:item.additive3="{ item }"><span class="font-monospace">{{ fmt(item.additive3) }}</span></template>
        <template v-slot:item.additive4="{ item }"><span class="font-monospace">{{ fmt(item.additive4) }}</span></template>
        <template v-slot:item.additive5="{ item }"><span class="font-monospace">{{ fmt(item.additive5) }}</span></template>
        <template v-slot:item.additive6="{ item }"><span class="font-monospace">{{ fmt(item.additive6) }}</span></template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>